import {localStorageWrapper} from '../../utils/storage/localStorageWrapper';
import type {AuthenticatedUserFragmentData} from '../../data-layer-client/queries/authenticatedUserFragment';
import type {AccountSessionClaim} from '../types';
import type {
  AuthenticationState,
  AuthenticationKeyType,
  KeyAndType,
} from './types';
import {notifyAuthStateChanged} from '../DataLayer';

const DEFAULT_TTL_MS = 604800000; // 7 days

const USER_SESSION_REFRESH_TOKEN = 'user_session_token';

interface AuthenticationKey {
  apiKey: string;
  expires: Date;
}

type AuthenticationKeyData =
  | {
      type: 'stripe_user';
      apiKey: string;
      userSessionRefreshToken?: string;
      user?: AuthenticatedUserFragmentData;
      publishableKey?: string;
      accountSessionClaim?: AccountSessionClaim;
    }
  | {
      type: 'account_session' | 'external_account';
      apiKey: string;
    };

export class AuthenticationStore {
  #keys: Partial<Record<AuthenticationKeyType, AuthenticationKey>> = {};

  #userSessionExpired = false;

  #user: AuthenticatedUserFragmentData | null = null;

  #publishableKey: string | undefined;

  #accountSessionClaim: AccountSessionClaim | undefined;

  state(): AuthenticationState {
    let key: AuthenticationKey | undefined;
    // eslint-disable-next-line no-cond-assign
    if ((key = this.getAndVerifyKey('stripe_user'))) {
      return {
        status: 'user_authenticated',
        expires: key.expires,
        user: this.#user,
        userAuthKey: key.apiKey,
        ...(this.#accountSessionClaim && this.#publishableKey
          ? {
              publishableKey: this.#publishableKey,
              accountSessionClaim: this.#accountSessionClaim,
            }
          : {}),
      };
      // eslint-disable-next-line no-cond-assign
    } else if ((key = this.getAndVerifyKey('account_session'))) {
      return {
        status: 'account_session_authenticated',
        expires: key.expires,
        userSessionExpired: this.#userSessionExpired,
      };
    } else {
      return {
        status: 'unauthenticated',
        userSessionExpired: this.#userSessionExpired,
      };
    }
  }

  /**
   * NOTE: This should *never* be passed back up through the iFrame. Use store.state() for that instead.
   * @returns The current API key used to talk to the backend service, if any.
   */
  getApiKey(type?: AuthenticationKeyType): KeyAndType | undefined {
    let key: AuthenticationKey | undefined;
    if (type) {
      // eslint-disable-next-line no-cond-assign
      if ((key = this.getAndVerifyKey(type))) {
        // use specified type when if present
        return {key: key.apiKey, type};
      } else {
        // if specified type is not present, return undefined
        return undefined;
      }
      // eslint-disable-next-line no-cond-assign
    } else if ((key = this.getAndVerifyKey('stripe_user'))) {
      // use stripe user key if present
      return {key: key.apiKey, type: 'stripe_user'};
      // eslint-disable-next-line no-cond-assign
    } else if ((key = this.getAndVerifyKey('account_session'))) {
      // use account session key if present
      return {key: key.apiKey, type: 'account_session'};
    }
  }

  getUserRefreshToken(): string | null {
    return localStorageWrapper.getItem(USER_SESSION_REFRESH_TOKEN);
  }

  persist(apiKeyData: AuthenticationKeyData, expiresAt?: Date): void {
    const expires = expiresAt || new Date(Date.now() + DEFAULT_TTL_MS);
    this.#keys[apiKeyData.type] = {apiKey: apiKeyData.apiKey, expires};

    if (apiKeyData.type === 'stripe_user') {
      this.#userSessionExpired = false;
      this.#user = apiKeyData.user || null;
      this.#publishableKey = apiKeyData.publishableKey;
      this.#accountSessionClaim = apiKeyData.accountSessionClaim;
      if (apiKeyData.userSessionRefreshToken != null) {
        localStorageWrapper.setItem(
          USER_SESSION_REFRESH_TOKEN,
          apiKeyData.userSessionRefreshToken,
        );
      }
    }
  }

  setUser(user: AuthenticatedUserFragmentData): void {
    this.#user = user;
  }

  clear(keyType?: AuthenticationKeyType): void {
    if (keyType) {
      delete this.#keys[keyType];
      if (keyType === 'stripe_user') {
        this.#userSessionExpired = true;
        this.#user = null;
        this.#publishableKey = undefined;
        this.#accountSessionClaim = undefined;
        localStorageWrapper.removeItem(USER_SESSION_REFRESH_TOKEN);
      }
    } else {
      this.#keys = {};
      this.#publishableKey = undefined;
      this.#accountSessionClaim = undefined;
      localStorageWrapper.removeItem(USER_SESSION_REFRESH_TOKEN);
    }
  }

  private getAndVerifyKey(
    type: AuthenticationKeyType,
  ): AuthenticationKey | undefined {
    let key = this.#keys[type];
    // If the key is expired, remove it.
    if (key && Date.now() >= key.expires.getTime()) {
      delete this.#keys[type];
      key = undefined;
      if (type === 'stripe_user') {
        this.#userSessionExpired = true;
      }
      notifyAuthStateChanged();
    }
    return key;
  }
}

export const authStore = new AuthenticationStore();
